'use client';

import { useIntl } from 'react-intl';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { useInstallmentCalculatorTracking } from '../../../Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';
import { Content } from '../Content/Content';
import { usePriceFormater } from '../../../../hooks/usePriceFormater';
import { BadgeDV } from '../../../BadgeDV/BadgeDV';

export type SideBySideWithTippBadgeProps = {
  /**
   * the single installment value from the calculation result as float (e.g. 13.37)
   */
  singleInstallment: number;
  /**
   * the currency string from the calculation result
   */
  currency: string;
  /**
   * the number of installments from the calculation result
   */
  numberOfInstallments: number;
  /**
   * open dialog handler
   */
  openInstallmentCalulatorHandler: () => void;
};

/**
 * Promotion component side by side with tipp badge
 *
 * component to promote the installment calculator in a side by side layout version with a tipp badge
 *
 * */
export const SideBySideWithTippBadge = ({
  singleInstallment,
  currency,
  numberOfInstallments,
  openInstallmentCalulatorHandler,
}: SideBySideWithTippBadgeProps) => {
  const { formatMessage } = useIntl();
  const formatPrice = usePriceFormater('INSTALLMENTCALCULATOR');

  const { trackGlycerinLinkClick } = useInstallmentCalculatorTracking();

  return (
    <div className="border-color-grey bg-color-background-base relative mt-1 block w-full rounded border">
      <div className="bg-color-background-base absolute left-1/2 block -translate-x-1/2 -translate-y-1/2 px-1">
        <BadgeDV
          text={formatMessage({
            id: 'ic.promotion.sideBySideWithTippBadge.chipLabel',
            defaultMessage: 'TIPP',
          })}
        />
      </div>

      <div className="flex flex-col gap-1 py-1">
        <div className="flex flex-row flex-nowrap gap-5 px-2">
          <Content
            smallText={formatMessage({
              id: 'ic.promotion.sideBySideWithTippBadge.from',
              defaultMessage: 'oder ab',
            })}
            bigText={formatPrice(singleInstallment, currency)}
          />
          <Content
            smallText={formatMessage({
              id: 'ic.promotion.sideBySideWithTippBadge.installments.prefix',
              defaultMessage: 'in',
            })}
            bigText={formatMessage(
              {
                id: 'ic.promotion.sideBySideWithTippBadge.installments',
                defaultMessage: '{installments} Raten',
              },
              {
                installments: numberOfInstallments,
              },
            )}
          />
        </div>
        <Divider />
        <button
          type="button"
          className="text-size-body-sm block text-center"
          onClick={() => {
            openInstallmentCalulatorHandler();
            trackGlycerinLinkClick();
          }}
        >
          {formatMessage({
            id: 'ic.promotion.sideBySideWithTippBadge.cta',
            defaultMessage: 'Wunschrate berechnen',
          })}
          {/* this extends the button click area over the whole component */}
          <span className="absolute inset-0" />
        </button>
      </div>
    </div>
  );
};
